import type { PointerEvent as ReactPointerEvent, ReactElement } from "react"
import { SceneControlIcon, type SceneControlIconName } from "@/components/locations/scene-control-icon"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import type { SceneThemeId } from "@/lib/location-scene-3d/scene-state"
import { cn } from "@/lib/utils"
import "@/components/locations/scene-controls.css"

export type CapturePhase = "idle" | "armed" | "recording" | "stopped"

type CaptureControlsProps = {
  skin: SceneThemeId
  phase: CapturePhase
  elapsedMs: number
  snapshotLabel: string
  recordLabel: string
  /** Frozen take length. Reopening an unsaved path stays on the save icon without a timer. */
  showElapsed?: boolean
  snapshotDisabled?: boolean
  recordDisabled?: boolean
  /** Forced hints for a new scene. Empty string hides that bubble. */
  snapshotHint?: string
  recordHint?: string
  onSnapshot: () => void
  onRecord: () => void
}

export const captureControlButtonClass = cn(
  "sc-btn m-0 box-border inline-flex size-[54px] shrink-0 appearance-none items-center justify-center",
  "rounded-lg border border-solid p-0",
  "cursor-pointer font-inherit leading-none shadow-none",
  "border-[var(--sc-border)] bg-[var(--sc-surface)] text-[color:var(--sc-fg)]",
  "transition-[background-color,border-color,color] duration-150",
  "hover:border-[var(--sc-border-hover)] hover:bg-[var(--sc-surface-hover)]",
  "disabled:pointer-events-none disabled:opacity-60",
  "data-[recording=true]:border-[var(--sc-record-border)] data-[recording=true]:bg-[var(--sc-record)]",
  "data-[recording=true]:disabled:opacity-100",
  "data-[recording=true]:text-[color:var(--sc-record-fg)]",
  "data-[recording=true]:hover:border-[var(--sc-record-border)] data-[recording=true]:hover:bg-[var(--sc-record)]",
)

function stopSceneEvent(event: { stopPropagation: () => void }) {
  event.stopPropagation()
}

function keepMouseFocusOff(event: ReactPointerEvent<HTMLButtonElement>) {
  event.preventDefault()
  event.stopPropagation()
}

export function formatCaptureTime(ms: number) {
  const total = Math.floor(Math.max(0, ms) / 1000)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor(total / 60) % 60
  const seconds = total % 60
  const pad = (value: number) => String(value).padStart(2, "0")
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`
}

function withHint(hint: string | undefined, button: ReactElement) {
  if (!hint) return button
  return (
    <ActionHintTooltip label={hint} open side="top" delayDuration={0} className="h-auto" contentClassName="z-[200]">
      {button}
    </ActionHintTooltip>
  )
}

function recordIcon(phase: CapturePhase): SceneControlIconName {
  if (phase === "recording") return "stop"
  if (phase === "stopped") return "save"
  return "video"
}

export function CaptureControls({
  skin,
  phase,
  elapsedMs,
  snapshotLabel,
  recordLabel,
  showElapsed = false,
  snapshotDisabled = false,
  recordDisabled = false,
  snapshotHint,
  recordHint,
  onSnapshot,
  onRecord,
}: CaptureControlsProps) {
  const showTimer = phase === "recording" || (phase === "stopped" && showElapsed)
  const recording = phase === "recording"

  return (
    <div
      className="scene-controls relative inline-flex items-center gap-[10px]"
      data-skin={skin}
      onPointerDown={stopSceneEvent}
      onPointerUp={stopSceneEvent}
      onClick={stopSceneEvent}
      onDoubleClick={stopSceneEvent}
      onWheel={stopSceneEvent}
      onContextMenu={stopSceneEvent}
    >
      {withHint(
        snapshotHint,
        <button
          type="button"
          className={captureControlButtonClass}
          aria-label={snapshotLabel}
          title={snapshotHint ? undefined : snapshotLabel}
          disabled={snapshotDisabled}
          onPointerDown={keepMouseFocusOff}
          onClick={() => onSnapshot()}
        >
          <SceneControlIcon name="camera" size={24} strokeWidth={1} />
        </button>,
      )}
      {withHint(
        recordHint,
        <button
          type="button"
          className={cn(captureControlButtonClass, phase === "armed" && "animate-pulse")}
          data-recording={recording ? "true" : "false"}
          aria-label={recordLabel}
          title={recordHint ? undefined : recordLabel}
          aria-pressed={recording || phase === "armed"}
          disabled={recordDisabled}
          onPointerDown={keepMouseFocusOff}
          onClick={() => onRecord()}
        >
          <SceneControlIcon name={recordIcon(phase)} size={24} strokeWidth={1} />
        </button>,
      )}
      {showTimer ? (
        <div
          className={cn(
            "absolute right-0 bottom-[calc(100%+10px)] flex items-center gap-1.5",
            "rounded-full border border-solid px-2.5 py-1",
            "border-[var(--sc-border)] bg-[var(--sc-surface)] text-xs leading-none text-[color:var(--sc-fg)]",
            "tabular-nums",
          )}
        >
          <span
            className={cn("size-2 shrink-0 rounded-full", recording ? "bg-[var(--sc-record)]" : "bg-[var(--sc-fg)]")}
          />
          <span>{formatCaptureTime(elapsedMs)}</span>
        </div>
      ) : null}
    </div>
  )
}
